import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion, increment } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import "./FoodDetail.css";

const FoodDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [food, setFood] = useState(null);
  const [averageScore, setAverageScore] = useState(0);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [irritabilityScore, setIrritabilityScore] = useState(5);
  const [comment, setComment] = useState("");
  const [nickname, setNickname] = useState("");
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          setNickname(userDoc.data().nickname);
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchFood = async () => {
      try {
        const foodRef = doc(db, "foods", id);
        const foodDoc = await getDoc(foodRef);

        if (foodDoc.exists()) {
          const foodData = foodDoc.data();
          setFood(foodData);

          if (foodData.reviews && foodData.reviews.length > 0) {
            const scores = foodData.reviews.map((review) => review.irritabilityScore);
            const avg = scores.reduce((sum, score) => sum + score, 0) / scores.length;
            setAverageScore(avg.toFixed(2));
          }
        }
      } catch (error) {
        console.error("Error fetching food:", error);
      }
    };

    fetchFood();
  }, [id]);

  const handleAddReview = async () => {
    if (!user) {
      alert("댓글을 작성하려면 로그인이 필요합니다.");
      navigate("/login");
      return;
    }

    if (!comment || irritabilityScore < 1 || irritabilityScore > 10) {
      alert("모든 필드를 올바르게 입력하세요!");
      return;
    }

    try {
      const foodRef = doc(db, "foods", id);
      const userRef = doc(db, "users", user.uid);

      // 새로운 리뷰 객체 생성
      const newReview = {
        nickname,
        irritabilityScore,
        comment,
      };

      // Firestore 업데이트
      await updateDoc(foodRef, {
        reviews: arrayUnion(newReview),
      });

      // 유저의 댓글 수 증가
      await updateDoc(userRef, {
        commentCount: increment(1),
      });

      // 로컬 상태 업데이트 (새로운 리뷰 추가)
      const updatedReviews = [...(food.reviews || []), newReview];
      const newAvgScore =
        updatedReviews.reduce((sum, review) => sum + review.irritabilityScore, 0) / updatedReviews.length;

      await updateDoc(foodRef, {
        avgScore: parseFloat(newAvgScore.toFixed(2)),
      });

      setFood((prevFood) => ({
        ...prevFood,
        reviews: updatedReviews,
        avgScore: newAvgScore,
      }));
      setAverageScore(newAvgScore.toFixed(2));

      alert("리뷰가 추가되었습니다!");
      setComment("");
      setIrritabilityScore(5);
      setIsReviewModalOpen(false);
    } catch (error) {
      console.error("Error adding review or updating avgScore:", error);
      alert("리뷰 추가 중 오류가 발생했습니다.");
    }
  };

  if (!food) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="food-detail-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        &lt;
      </button>
      <div className="food-header">
        <h1>{food.name}</h1>
        <div className="average-score-container">
          <h2>속편한 점수</h2>
          <div className="score-display">
            <span className="score-value">{averageScore}</span>
            <span className="score-label">/ 10</span>
          </div>
          <p className="score-description">
            점수가 높을수록 속편한 음식입니다.
          </p>
        </div>
      </div>

      <div className="reviews-section">
        <h2>댓글</h2>
        {food.reviews && food.reviews.length > 0 ? (
          <div className="comments-list">
            {food.reviews.map((review, index) => (
              <div key={index} className="comment">
                <strong className="nickname">{review.nickname}</strong>
                <p className="irritability-score">
                  속편한 점수: {review.irritabilityScore}
                </p>
                <p className="comment-text">{review.comment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-comments">아직 댓글이 없습니다.</p>
        )}
      </div>

      <button
        className="add-review-button"
        onClick={() => {
          if (user) {
            setIsReviewModalOpen(true);
          } else {
            alert("댓글을 작성하려면 로그인이 필요합니다.");
            navigate("/login");
          }
        }}
      >
        평가하기
      </button>

      {isReviewModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>리뷰 작성</h2>
            <p>속편한 점수 (1-10)</p>
            <input
              type="number"
              value={irritabilityScore}
              onChange={(e) => setIrritabilityScore(Number(e.target.value))}
              placeholder="과민성 수치 (1~10)"
              min="1"
              max="10"
              className="review-input"
            />
            <p>자신의 경험을 적어주세요!</p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="ex) 제가 가장 좋아하는 음식이에요! 시험이나 중요한 일 전에 같이 섞어서 먹으면 배가 하나도 안 아파요"
              className="review-textarea"
            />
            <div className="modal-buttons">
              <button
                className="close-button"
                onClick={() => setIsReviewModalOpen(false)}
              >
                닫기
              </button>
              <button className="add-button" onClick={handleAddReview}>
                제출
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodDetail;